import React, { useState } from "react";

import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from "reactstrap";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useAuth } from "context/AuthContext";
import FlaggedModal from "../../flagged/FlaggedModal";

const Header = ({ orderDetails, handleOrderStatusChange, goBack, isUpdating }) => {
  const { permission } = useAuth();
  const { t } = useTranslation();
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const toggleFlagModal = () => setIsFlagModalOpen(prev => !prev);
  if (!orderDetails) return null;

  const from = orderDetails.sender_listing_id?.fromPlace?.name || orderDetails.traveler_listing_id?.fromPlace?.name || "-";
  const to = orderDetails.sender_listing_id?.toPlace?.name || orderDetails.traveler_listing_id?.toPlace?.name || "-";
  const journeyRoute = `${from} → ${to}`;

  const journeyDateRaw =
    orderDetails.acceptedOffer?.departureAt ||
    orderDetails.acceptedOffer?.offerDetails?.departureAt ||
    orderDetails.accepted_offer_id?.departureAt ||
    orderDetails.accepted_offer_id?.offerDetails?.departureAt ||
    orderDetails.traveler_listing_id?.date ||
    orderDetails.sender_listing_id?.date ||
    orderDetails.traveler_listing_id?.documentDetails?.travelTime ||
    orderDetails.sender_listing_id?.pickUpDetails?.pickupTime ||
    null;

  const journeyDate = (journeyDateRaw && !isNaN(Date.parse(journeyDateRaw))) ? helpers.getFormattedDate(journeyDateRaw, t) : "-";
  const currentStatus = helpers.normalizeOrderStatus(orderDetails.status);
  const statusClass = helpers.getStatusClass(currentStatus, "order");
  const isDeleted = orderDetails?.deleted === true;

  return (
    <Card className="shadow-sm mb-3">
      <CardBody className="py-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-2">
          <h5 className="mb-3 mb-md-0 font-weight-bold text-lg mt-0 text-center text-md-left">
            {t("page.orders.orderDetail", "Order Details")}
          </h5>

          <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-end">
            {/* Status Dropdown */}
            <UncontrolledDropdown>
              <DropdownToggle
                caret
                size="sm"
                className={`status-dropdown ${statusClass} mb-2 mb-md-0`}
                id={`statusDropdown-${orderDetails.orderid}`}
                disabled={!permission.permissions.modules.orders.update || isDeleted || isUpdating}
              >
                {t(`page.orders.status.${currentStatus}`, helpers.ORDER_STATUS_API_MAP[currentStatus] || currentStatus)}
              </DropdownToggle>

              <DropdownMenu>
                {Object.keys(helpers.ORDER_STATUSES).map((status) => (
                  <DropdownItem
                    key={status}
                    disabled={
                      status === currentStatus || !permission.permissions.modules.orders.update ||
                      isDeleted || isUpdating
                    }
                    onClick={() =>
                      permission.permissions.modules.orders.update &&
                      handleOrderStatusChange(
                        orderDetails,
                        helpers.ORDER_STATUS_API_MAP[status]
                      )
                    }
                  >
                    {t(`page.orders.status.${status}`, helpers.ORDER_STATUS_API_MAP[status] || status)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            <UncontrolledTooltip target={`statusDropdown-${orderDetails.orderid}`}>
              {!permission.permissions.modules.orders.update
                ? t("tooltip.noPermission", "Permission denied")
                : isDeleted
                ? t("page.orders.buttons.orderDeleted", "Deleted order")
                : t("modal.orders.updateStatusTitle.default", "Are you sure you want to change the delivery status?")}
            </UncontrolledTooltip>

            {currentStatus === helpers.ORDER_STATUS_API_MAP.failed.toLowerCase() && (
              <Button
                color="warning"
                size="sm"
                className="ml-2 mb-2 mb-md-0"
                disabled={isDeleted}
                onClick={toggleFlagModal}
              >
                {t("page.flagged.flagThisContent", "Flag this content")}
              </Button>
            )}

            <Button color="secondary" size="sm" className="ml-2 mb-2 mb-md-0" onClick={goBack}>
              {t("page.messages.back", "Back")}
            </Button>
          </div>
        </div>

        <Row className="align-items-center text-center mt-4">
          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("page.orders.orderId", "Order ID")}
            </small>
            <div className="text-sm font-weight-bold">
              {orderDetails.orderid || "-"}
            </div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("page.orders.journeyDate", "Journey Date")}
            </small>
            <div className="text-sm font-weight-bold">{journeyDate}</div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("table.headers.location", "Location")}
            </small>
            <div className="text-sm font-weight-bold">{journeyRoute}</div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("page.orders.otp", "OTP")}
            </small>
            <div className="text-sm font-weight-bold">
              {orderDetails.otp_code || "NA"}
            </div>
          </Col>

        </Row>

        {/* Flagged Modal */}
        <FlaggedModal
          isOpen={isFlagModalOpen}
          toggle={toggleFlagModal}
          contentCategory="order"
          contentId={orderDetails._id}
          user={orderDetails.user_id || {}}
        />
      </CardBody>
    </Card>
  );
};

export default Header;
